// ✅ src/components/screens/StoreReviewsScreen.js
// ✅ SAME LOGIC (Firestore + routes + fields) — UI upgraded
// ✅ Desktop: sticky "Leave a review" panel stays visible while scrolling
// ✅ NEW: Reviews list has its OWN scroll (web + mobile) — page itself doesn't scroll

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";

function clampToHalf(n) {
  const x = Number(n || 0);
  if (Number.isNaN(x)) return 0;
  return Math.max(0, Math.min(5, Math.round(x * 2) / 2));
}

function Star({ fill = 0, size = 18 }) {
  const id = useMemo(() => `g-${Math.random().toString(36).slice(2)}`, []);
  const pct = fill === 1 ? 100 : fill === 0.5 ? 50 : 0;

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block" }}>
      <defs>
        <linearGradient id={id}>
          <stop offset={`${pct}%`} stopColor="#fbbf24" />
          <stop offset={`${pct}%`} stopColor="#e5e7eb" />
        </linearGradient>
      </defs>
      <path
        d="M12 17.3l-6.18 3.73 1.64-7.03L2 9.24l7.19-.61L12 2l2.81 6.63 7.19.61-5.46 4.76 1.64 7.03z"
        fill={`url(#${id})`}
        stroke="#d1d5db"
        strokeWidth="1"
      />
    </svg>
  );
}

function StarRating({ value, onChange, size = 20, readOnly = false }) {
  const v = clampToHalf(value);

  const stars = [1, 2, 3, 4, 5].map((i) => {
    const diff = v - (i - 1);
    const fill = diff >= 1 ? 1 : diff >= 0.5 ? 0.5 : 0;

    return (
      <button
        key={i}
        type="button"
        disabled={readOnly}
        onClick={() => {
          if (readOnly) return;
          onChange?.(i);
        }}
        onMouseMove={(e) => {
          if (readOnly) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const isLeft = e.clientX - rect.left < rect.width / 2;
          const next = isLeft ? i - 0.5 : i;
          onChange?.(next);
        }}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: readOnly ? "default" : "pointer",
          lineHeight: 0,
        }}
        aria-label={`Rate ${i} stars`}
        title={readOnly ? undefined : "Move left/right for half stars"}
      >
        <Star fill={fill} size={size} />
      </button>
    );
  });

  return <div style={{ display: "flex", gap: 6, alignItems: "center" }}>{stars}</div>;
}

function Avatar({ name }) {
  const n = String(name || "").trim();
  const parts = n.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "A";
  const b = parts[1]?.[0] || "";
  const initials = (a + b).toUpperCase();

  return (
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: 999,
        background:
          "radial-gradient(120% 120% at 30% 20%, rgba(99,102,241,0.25), rgba(14,165,233,0.18) 40%, rgba(15,23,42,0.06) 100%)",
        border: "1px solid rgba(148,163,184,0.45)",
        display: "grid",
        placeItems: "center",
        fontWeight: 1100,
        color: "#0f172a",
        letterSpacing: 0.5,
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

export default function StoreReviewsScreen() {
  const navigate = useNavigate();
  const { storeId } = useParams(); // ✅ /reviews/store/:storeId

  const [user, setUser] = useState(null);
  const [rating, setRating] = useState(4.5);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [snapErr, setSnapErr] = useState("");

  const targetType = "store";
  const targetId = String(storeId || "").trim();

  // ✅ responsive
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // ✅ listen to this store's reviews
  useEffect(() => {
    if (!targetId) return;

    setSnapErr("");

    const q = query(
      collection(db, "reviews"),
      where("targetType", "==", targetType),
      where("targetId", "==", targetId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReviews(rows);
      },
      (err) => {
        console.error("reviews snapshot error:", err);
        setSnapErr(err?.message || "Failed to load reviews.");
      }
    );

    return () => unsub();
  }, [targetId]);

  const avg = useMemo(() => {
    if (!reviews.length) return 0;
    const sum = reviews.reduce((a, r) => a + Number(r.rating || 0), 0);
    return clampToHalf(sum / reviews.length);
  }, [reviews]);

  const submit = async () => {
    const t = String(text || "").trim();
    const r = clampToHalf(rating);

    if (!targetId) return alert("Missing store id.");
    if (!user) return alert("Please login to write a review.");
    if (!t) return alert("Write something small for the review.");

    setSaving(true);
    try {
      const name = user.displayName || user.email?.split("@")?.[0] || "Anonymous";

      await addDoc(collection(db, "reviews"), {
        targetType, // ✅ store
        targetId, // ✅ storeId from URL
        rating: r,
        text: t,
        userId: user.uid,
        userName: name,
        createdAt: serverTimestamp(),
      });

      setText("");
      setRating(4.5);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to submit review. Check console.");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = useMemo(() => {
    const t = String(text || "").trim();
    return !!targetId && !!user && !!t && !saving;
  }, [targetId, user, text, saving]);

  const S = {
    page: {
      minHeight: "100vh",
      height: "100vh",              // ✅ lock to viewport height
      overflow: "hidden",           // ✅ prevent page scrolling
      padding: 18,
      color: "#0f172a",
      background:
        "radial-gradient(1200px 600px at 20% -10%, rgba(99,102,241,0.18), transparent 55%), radial-gradient(900px 500px at 90% 10%, rgba(14,165,233,0.14), transparent 55%), linear-gradient(180deg, #f8fafc, #f1f5f9)",
      display: "flex",
      justifyContent: "center",
    },
    wrap: { width: "100%", maxWidth: 1120, height: "100%" },

    shell: {
      borderRadius: 26,
      border: "1px solid rgba(148,163,184,0.45)",
      background: "rgba(255,255,255,0.75)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 18px 50px rgba(15,23,42,0.10)",
      overflow: "hidden",
      height: "100%",              // ✅ fill viewport (inside padding)
      display: "flex",             // ✅ header fixed, body grows
      flexDirection: "column",
    },

    header: {
      padding: 16,
      borderBottom: "1px solid rgba(148,163,184,0.35)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.90), rgba(255,255,255,0.62))",
      flex: "0 0 auto",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    backBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      borderRadius: 999,
      border: "1px solid rgba(148,163,184,0.55)",
      background: "rgba(255,255,255,0.9)",
      padding: "10px 12px",
      fontWeight: 1100,
      cursor: "pointer",
      color: "#0f172a",
      boxShadow: "0 12px 26px rgba(15,23,42,0.06)",
    },
    title: { margin: 0, fontSize: 20, fontWeight: 1200, letterSpacing: -0.2 },
    subtitle: {
      margin: 0,
      marginTop: 4,
      color: "#475569",
      fontWeight: 800,
      fontSize: 13,
      lineHeight: 1.5,
    },
    pills: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 12px",
      borderRadius: 999,
      border: "1px solid rgba(148,163,184,0.45)",
      background: "rgba(255,255,255,0.9)",
      fontWeight: 1100,
      fontSize: 12,
      boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
      color: "#0f172a",
    },
    err: {
      marginTop: 12,
      padding: 12,
      borderRadius: 16,
      border: "1px solid rgba(244,63,94,0.35)",
      background: "rgba(255,255,255,0.9)",
      color: "#9f1239",
      fontWeight: 1000,
      boxShadow: "0 14px 36px rgba(15,23,42,0.08)",
    },

    body: {
      padding: 16,
      display: "grid",
      gap: 14,
      flex: "1 1 auto",            // ✅ take remaining height
      overflow: "hidden",          // ✅ body itself doesn't scroll
      minHeight: 0,                // ✅ IMPORTANT for nested scroll areas
    },

    // ✅ 2 columns on desktop, rows on mobile, and FULL HEIGHT
    grid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "420px 1fr",
      gridTemplateRows: isMobile ? "auto 1fr" : undefined, // ✅ mobile: composer then list fills rest
      gap: 14,
      alignItems: "start",
      height: "100%",              // ✅ needed for nested scroll
      minHeight: 0,
    },

    // ✅ sticky wrapper for composer (desktop)
    stickyWrap: {
      position: isMobile ? "static" : "sticky",
      top: isMobile ? "auto" : 14,
      alignSelf: "start",
      zIndex: 2,
    },

    composer: {
      borderRadius: 22,
      border: "1px solid rgba(148,163,184,0.50)",
      background: "rgba(255,255,255,0.92)",
      boxShadow: "0 18px 44px rgba(15,23,42,0.08)",
      padding: 16,
      display: "grid",
      gap: 12,
    },
    compTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
    },
    compTitle: { fontSize: 14, fontWeight: 1200, margin: 0 },
    hint: { fontSize: 12, color: "#64748b", fontWeight: 800, margin: 0 },

    ratingRow: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
    scoreChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 10px",
      borderRadius: 999,
      border: "1px solid rgba(148,163,184,0.45)",
      background: "rgba(2,6,23,0.04)",
      fontWeight: 1100,
      fontSize: 12,
    },

    textarea: {
      width: "100%",
      minHeight: 120,
      borderRadius: 18,
      border: "1px solid rgba(148,163,184,0.60)",
      background: "rgba(255,255,255,0.98)",
      padding: 14,
      outline: "none",
      resize: "vertical",
      fontSize: 14,
      lineHeight: 1.65,
      color: "#0f172a",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
    },

    actions: {
      display: "grid",
      gap: 10,
    },
    note: { fontSize: 12, color: "#64748b", fontWeight: 800 },
    btn: {
      borderRadius: 16,
      border: "1px solid rgba(15,23,42,0.10)",
      background: "linear-gradient(180deg, #0f172a, #0b1220)",
      color: "#fff",
      fontWeight: 1200,
      padding: "12px 14px",
      cursor: "pointer",
      boxShadow: "0 18px 40px rgba(15,23,42,0.22)",
      width: "100%",
    },
    btnDisabled: { opacity: 0.6, cursor: "not-allowed", boxShadow: "none" },

    // ✅ REVIEWS LIST SCROLL AREA
    listWrap: {
      minHeight: 0,                 // ✅ allow it to shrink inside grid
      height: "100%",
      overflowY: "auto",            // ✅ THIS is the scroll
      paddingRight: 6,              // ✅ little space so scrollbar doesn't kiss cards
      WebkitOverflowScrolling: "touch",
    },

    list: { display: "grid", gap: 12 },

    item: {
      borderRadius: 22,
      border: "1px solid rgba(148,163,184,0.45)",
      background: "rgba(255,255,255,0.90)",
      boxShadow: "0 18px 44px rgba(15,23,42,0.07)",
      padding: 14,
    },
    itemTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    left: { display: "flex", gap: 12, alignItems: "flex-start", minWidth: 260 },
    name: { fontWeight: 1200, fontSize: 14, margin: 0 },
    time: { color: "#64748b", fontSize: 12, fontWeight: 900, margin: 0 },
    text: {
      marginTop: 10,
      color: "#0f172a",
      lineHeight: 1.75,
      fontSize: 14,
      whiteSpace: "pre-wrap",
    },
    empty: {
      textAlign: "center",
      color: "#64748b",
      padding: 22,
      borderRadius: 22,
      border: "1px dashed rgba(148,163,184,0.75)",
      background: "rgba(255,255,255,0.60)",
      fontWeight: 900,
    },
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.shell}>
          <div style={S.header}>
            <div style={S.headerRow}>
              <button style={S.backBtn} onClick={() => navigate(-1)}>
                ← Back
              </button>

              <div style={{ minWidth: 220 }}>
                <h1 style={S.title}>Store Reviews</h1>
                <p style={S.subtitle}>Read feedback — and drop yours without losing the box 😄</p>
              </div>

              <div style={S.pills}>
                <div style={S.pill}>
                  <span style={{ fontSize: 14 }}>⭐</span>
                  <span>Average</span>
                  <span style={{ fontWeight: 1300 }}>{avg.toFixed(1)} / 5</span>
                </div>
                <div style={S.pill}>
                  <span style={{ fontSize: 14 }}>📝</span>
                  <span>
                    {reviews.length} review{reviews.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
            </div>

            {snapErr ? <div style={S.err}>⚠️ {snapErr}</div> : null}
          </div>

          <div style={S.body}>
            <div style={S.grid}>
              {/* ✅ STICKY WRITE-REVIEW PANEL */}
              <div style={S.stickyWrap}>
                <div style={S.composer}>
                  <div style={S.compTop}>
                    <p style={S.compTitle}>Leave a review</p>
                    <p style={S.hint}>Hover left/right for 0.5 ⭐</p>
                  </div>

                  <div style={S.ratingRow}>
                    <StarRating value={rating} onChange={setRating} />
                    <div style={S.scoreChip}>
                      <span style={{ opacity: 0.8 }}>Score</span>
                      <span style={{ fontWeight: 1300 }}>
                        {clampToHalf(rating).toFixed(1)}
                      </span>
                    </div>
                    {!user ? (
                      <div style={{ ...S.scoreChip, borderStyle: "dashed" }}>
                        Login required
                      </div>
                    ) : null}
                  </div>

                  <textarea
                    style={S.textarea}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Be honest and specific. What did you like? What should improve?"
                  />

                  <div style={S.actions}>
                    <div style={S.note}>
                      {user ? (
                        <>
                          Posting as{" "}
                          <b>{user.displayName || user.email?.split("@")?.[0] || "Anonymous"}</b>
                        </>
                      ) : (
                        <>Login to submit a review.</>
                      )}
                    </div>

                    <button
                      style={{ ...S.btn, ...(canSubmit ? null : S.btnDisabled) }}
                      onClick={submit}
                      disabled={!canSubmit}
                    >
                      {saving ? "Submitting..." : "Submit review"}
                    </button>
                  </div>
                </div>
              </div>

              {/* ✅ REVIEWS LIST (OWN SCROLL) */}
              <div style={S.listWrap}>
                <div style={S.list}>
                  {reviews.map((r) => {
                    const when = r?.createdAt?.toDate?.()
                      ? r.createdAt.toDate().toLocaleString()
                      : "";

                    return (
                      <div key={r.id} style={S.item}>
                        <div style={S.itemTop}>
                          <div style={S.left}>
                            <Avatar name={r.userName || "Anonymous"} />
                            <div style={{ display: "grid", gap: 4 }}>
                              <p style={S.name}>{r.userName || "Anonymous"}</p>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                <StarRating value={r.rating || 0} readOnly size={18} />
                                <div style={{ fontWeight: 1100 }}>
                                  {clampToHalf(r.rating || 0).toFixed(1)}
                                </div>
                              </div>
                            </div>
                          </div>

                          <p style={S.time}>{when}</p>
                        </div>

                        <div style={S.text}>{String(r.text || "")}</div>
                      </div>
                    );
                  })}

                  {!reviews.length && <div style={S.empty}>No reviews yet. Be the first 👀</div>}
                </div>
              </div>
              {/* ✅ end list scroll */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
